import { Component, signal, computed, ViewChild, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormArray, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { ChartOfAccountTypeaheadComponent } from '../../../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';
import { AmountInput } from '../../../../parts/amount-input/amount-input.component';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ManualGLEntryMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { CreateManualLedgerEntriesRequest } from '../../../../core/models/data/ledger-entry.model';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
	selector: 'ms-manual-gl-entries',
	standalone: true,
	imports: [
		PageTitleComponent,
		CommonModule,
		ReactiveFormsModule,
		FaDuotoneIconComponent,
		ConfirmationModalComponent,
		ChartOfAccountTypeaheadComponent,
		AmountInput,
		InputLabel,
		DatePlaceholderDirective
],
	templateUrl: './manual-gl-entries.page.html',
	styleUrl: './manual-gl-entries.page.scss'
})
export class ManualGLEntriesPage extends BaseFormComponent implements OnDestroy, CanComponentDeactivate {

	//#region Properties & Signals

	public pageForm: FormGroup;
	public rowIds: number[] = [];
	private nextRowId = 0;
	private rowSubscriptions: Subscription[] = [];
	public submissionErrorMessage = signal<string | null>(null);

	@ViewChild('glConfirmationModalRef') glConfirmationModalRef!: ConfirmationModalComponent;

	private dirtyFormRegistry = inject(DirtyFormRegistryService);
	private destroyRef = inject(DestroyRef);

	//#endregion Properties & Signals

	//#region Computed

	public totalDebits = computed(() => {
		const entries = this.getEntriesRawValues();
		return entries
			.filter(e => e.isDebit === true)
			.reduce((sum, e) => sum + this.parseAmount(e.amount), 0);
	});

	public totalCredits = computed(() => {
		const entries = this.getEntriesRawValues();
		return entries
			.filter(e => e.isDebit === false)
			.reduce((sum, e) => sum + this.parseAmount(e.amount), 0);
	});

	public difference = computed(() => {
		return Math.abs(this.totalDebits() - this.totalCredits());
	});

	public isBalanced = computed(() => {
		return this.difference() < 0.001 && this.totalDebits() > 0;
	});

	//#endregion Computed

	//#region Constructor & Initialization

	constructor(
		private fb: FormBuilder,
		private financialService: FinancialService,
		public userSessionService: UserSessionService,
		private bootstrapService: BootstrapService,
		private router: Router
	) {
		super();

		this.dirtyFormRegistry.register(this);
		this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

		this.pageForm = this.fb.group({
			effectiveDate: ['', [Validators.required]],
			note: [''],
			entries: this.fb.array([])
		});

		this.addRow();
	}

	//#endregion Constructor & Initialization

	//#region FormArray Management

	public get entries(): FormArray {
		return this.pageForm.get('entries') as FormArray;
	}

	// Signal to trigger computed recalculation when form values change
	private entriesVersion = signal(0);

	public addRow(): void {
		const row = this.fb.group({
			chartOfAccountsId: [null, [Validators.required]],
			isDebit: [true],
			amount: ['', [Validators.required, Validators.min(0.01)]],
			payee: ['']
		});

		const sub = row.valueChanges.subscribe(() => {
			this.entriesVersion.update(v => v + 1);
		});

		this.entries.push(row);
		this.rowIds.push(this.nextRowId++);
		this.rowSubscriptions.push(sub);
	}

	public removeRow(index: number): void {
		if (this.entries.length > 1) {
			this.rowSubscriptions[index]?.unsubscribe();
			this.rowSubscriptions.splice(index, 1);
			this.entries.removeAt(index);
			this.rowIds.splice(index, 1);
			this.entriesVersion.update(v => v + 1);
		}
	}

	public getEntryGroup(index: number): FormGroup {
		return this.entries.at(index) as FormGroup;
	}

	public toggleDebitCredit(index: number): void {
		const control = this.getEntryGroup(index).get('isDebit');
		control?.setValue(!control.value);
	}

	//#endregion FormArray Management

	//#region Form Submission

	public async onSubmit(): Promise<void> {
		this.submissionErrorMessage.set(null);

		if (this.pageForm.invalid) {
			this.pageForm.markAllAsTouched();
			return;
		}

		if (!this.isBalanced()) {
			this.submissionErrorMessage.set(this.FormValidatorMessages.ManualGLEntryUnbalanced);
			return;
		}

		const orgId = this.userSessionService.activeOrganization()?.organizationId;
		if (!orgId) return;

		this.isSubmitting = true;
		try {
			const request = this.buildSubmissionRequest(orgId);
			const result = await this.financialService.createManualLedgerEntries(request);

			if (result.success) {
				this.submitSucceeded = true;
				this.glConfirmationModalRef.modalType.set(ModalTypes.Success);
				this.glConfirmationModalRef.message.set(
					result.message || ManualGLEntryMessagingConstants.MANUAL_GL_ENTRY_CREATE_SUCCESS()
				);
				this.glConfirmationModalRef.cancelButtonText.set('Leave');
				this.glConfirmationModalRef.confirmButtonText.set('Create Another');
				this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Show);
			} else {
				this.submissionErrorMessage.set(
					result.message || ManualGLEntryMessagingConstants.MANUAL_GL_ENTRY_CREATE_FAILURE()
				);
			}
		} catch (error) {
			console.error('Exception caught:', error);
			this.submissionErrorMessage.set(ManualGLEntryMessagingConstants.MANUAL_GL_ENTRY_CREATE_FAILURE());
		} finally {
			this.isSubmitting = false;
		}
	}

	private buildSubmissionRequest(organizationId: number): CreateManualLedgerEntriesRequest {
		const formValue = this.pageForm.value;

		return {
			organizationId,
			effectiveDate: formValue.effectiveDate,
			note: formValue.note?.trim() || undefined,
			entries: formValue.entries.map((entry: any) => ({
				chartOfAccountsId: entry.chartOfAccountsId,
				isDebit: entry.isDebit,
				amount: CurrencyUtilities.convertCurrencyStringToMinorUnits(String(entry.amount || '0'), 100),
				payee: entry.payee?.trim() || undefined
			}))
		};
	}

	//#endregion Form Submission

	//#region UI Helper Methods

	public onCancel(): void {
		if (this.pageForm.dirty) {
			this.glConfirmationModalRef.modalType.set(ModalTypes.Question);
			this.glConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Show);
		} else {
			this.router.navigate(['/dashboard']);
		}
	}

	public canDeactivate(): boolean | Promise<boolean> {
		return this.awaitLeaveConfirmation(this.pageForm.dirty && !this.submitSucceeded, () => {
			this.glConfirmationModalRef.modalType.set(ModalTypes.Question);
			this.glConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Show);
		});
	}

	public resetForm(): void {
		this.submitSucceeded = false;
		while (this.entries.length > 1) {
			const lastIndex = this.entries.length - 1;
			this.rowSubscriptions[lastIndex]?.unsubscribe();
			this.rowSubscriptions.pop();
			this.entries.removeAt(lastIndex);
			this.rowIds.pop();
		}

		this.entries.at(0).reset({
			chartOfAccountsId: null,
			isDebit: true,
			amount: '',
			payee: ''
		});

		this.pageForm.patchValue({ effectiveDate: '', note: '' });
		this.pageForm.markAsPristine();
		this.pageForm.markAsUntouched();
		this.submissionErrorMessage.set(null);
	}

	public formatCurrency(amount: number): string {
		const minorAmount = CurrencyUtilities.convertMajorUnitsToMinor(amount, this.userSessionService.currentMinorUnits());
		return CurrencyUtilities.convertMinorUnitToDisplayFormat(minorAmount, this.userSessionService.currentCurrency());
	}

	//#endregion UI Helper Methods

	//#region Modal Management

	public confirmationConfirmFunction(): void {
		if (this.resolvePendingLeave(true)) {
			this.resetForm();
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Hide);
			return;
		}

		if (this.glConfirmationModalRef.modalType() === ModalTypes.Success) {
			this.resetForm();
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Hide);
		} else {
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Hide);
			this.router.navigate(['/dashboard']);
		}
	}

	public confirmationCancelFunction(): void {
		if (this.resolvePendingLeave(false)) {
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Hide);
			return;
		}

		if (this.glConfirmationModalRef.modalType() === ModalTypes.Success) {
			this.resetForm();
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Hide);
			this.router.navigate(['/dashboard']);
		} else {
			this.bootstrapService.manageModal('#glConfirmationModal', ModalActions.Hide);
		}
	}

	//#endregion Modal Management

	//#region Private Methods

	private getEntriesRawValues(): any[] {
		this.entriesVersion();
		return this.entries.controls.map(c => c.value);
	}

	private parseAmount(value: any): number {
		if (!value) return 0;
		const parsed = parseFloat(String(value).replace(/,/g, ''));
		return isNaN(parsed) ? 0 : parsed;
	}

	//#endregion Private Methods

	//#region Lifecycle

	ngOnDestroy(): void {
		this.rowSubscriptions.forEach(sub => sub.unsubscribe());
	}

	//#endregion Lifecycle
}
